import { useMemo, useState } from 'react';
import type { PreviewResult, SourceKind } from '../api';
import { Copy, Empty } from './ui';

/**
 * The payload explorer.
 *
 * Its whole reason for existing is that writing a JSON path or CSS selector by hand against
 * an unfamiliar response is where people give up. Everything here is clickable: picking a
 * row writes the expression into the field being edited.
 */

export interface Pick {
  extractor: 'json' | 'css';
  expr: string;
  attr?: string;
  sample: string;
}

/**
 * Turn a text node into a CSS selector that will find it again.
 *
 * Prefers an id, then a distinctive class, and falls back to nth-of-type — walking up only
 * as far as it takes to be unique, so the selector survives unrelated changes to the page.
 */
function selectorFor(el: Element, root: Document): string | null {
  const parts: string[] = [];
  let node: Element | null = el;

  while (node && node !== root.documentElement) {
    if (node.id && root.querySelectorAll(`#${CSS.escape(node.id)}`).length === 1) {
      parts.unshift(`#${CSS.escape(node.id)}`);
      break;
    }

    const tag = node.tagName.toLowerCase();
    const classes = Array.from(node.classList).filter((c) => /^[a-z][\w-]*$/i.test(c));
    let part = tag;

    for (const c of classes.slice(0, 2)) {
      const candidate = `${tag}.${CSS.escape(c)}`;
      if (root.querySelectorAll(candidate).length === 1) {
        part = candidate;
        break;
      }
      part = `${part}.${CSS.escape(c)}`;
    }

    const parent: Element | null = node.parentElement;
    if (parent) {
      const siblings = Array.from(parent.children).filter((s) => s.tagName === node!.tagName);
      if (siblings.length > 1) part += `:nth-of-type(${siblings.indexOf(node) + 1})`;
    }

    parts.unshift(part);
    const selector = parts.join(' > ');
    try {
      if (root.querySelectorAll(selector).length === 1) return selector;
    } catch {
      return null;
    }
    node = parent;
  }

  const selector = parts.join(' > ');
  return selector || null;
}

interface HtmlCandidate {
  selector: string;
  text: string;
}

function htmlCandidates(html: string, limit = 300): HtmlCandidate[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walker = doc.createTreeWalker(doc.body ?? doc, NodeFilter.SHOW_TEXT);
  const out: HtmlCandidate[] = [];
  const seen = new Set<string>();

  while (walker.nextNode() && out.length < limit) {
    const node = walker.currentNode;
    const text = (node.textContent ?? '').trim();
    // Anything without a digit is rarely what a Virtual HTTP Input wants, and skipping it
    // keeps the list short enough to scan.
    if (text.length === 0 || text.length > 120 || !/\d/.test(text)) continue;

    const el = node.parentElement;
    if (!el || ['SCRIPT', 'STYLE', 'NOSCRIPT'].includes(el.tagName)) continue;

    const selector = selectorFor(el, doc);
    if (!selector || seen.has(selector)) continue;
    seen.add(selector);
    out.push({ selector, text });
  }
  return out;
}

export function PreviewPanel({
  result,
  kind,
  onPick,
  pending,
}: {
  result: PreviewResult | null;
  kind: SourceKind;
  onPick: (pick: Pick) => void;
  pending: boolean;
}) {
  const [filter, setFilter] = useState('');
  const [tab, setTab] = useState<'pick' | 'raw'>('pick');

  const candidates = useMemo(() => {
    if (!result?.sample) return [];
    if (kind === 'json') {
      return (result.hints ?? []).map((h) => ({
        expr: h.path,
        sample: String(h.value),
        extractor: 'json' as const,
      }));
    }
    if (kind === 'html' || kind === 'xml') {
      try {
        return htmlCandidates(result.sample).map((c) => ({
          expr: c.selector,
          sample: c.text,
          extractor: 'css' as const,
        }));
      } catch {
        return [];
      }
    }
    return [];
  }, [result, kind]);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return candidates.slice(0, 400);
    return candidates
      .filter((c) => c.expr.toLowerCase().includes(needle) || c.sample.toLowerCase().includes(needle))
      .slice(0, 400);
  }, [candidates, filter]);

  if (pending) return <Empty>Fetching…</Empty>;
  if (!result) return <Empty>Fetch the URL to explore what it returns.</Empty>;

  if (result.error) {
    return (
      <div className="text-sm text-bad bg-bad/10 border border-bad/30 rounded-md px-3 py-2 break-words">
        {result.error}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-xs text-ink-500 flex-wrap">
        <span className={result.ok ? 'text-good' : 'text-bad'}>HTTP {result.status}</span>
        <span>{result.durationMs} ms</span>
        <span>{result.bytes?.toLocaleString()} bytes</span>
        {result.truncated && <span className="text-warn">truncated</span>}
        {(result.redirectChain?.length ?? 0) > 1 && (
          <span title={result.redirectChain?.join(' → ')}>{(result.redirectChain?.length ?? 1) - 1} redirect(s)</span>
        )}
        <div className="ml-auto flex gap-1">
          {(['pick', 'raw'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-2 py-0.5 rounded ${tab === t ? 'bg-ink-800 text-ink-100' : 'text-ink-500 hover:text-ink-300'}`}
            >
              {t === 'pick' ? 'Pick a value' : 'Raw'}
            </button>
          ))}
        </div>
      </div>

      {result.parseError && (
        <div className="text-sm text-warn bg-warn/10 border border-warn/30 rounded-md px-3 py-2">
          {result.parseError}
        </div>
      )}

      {tab === 'raw' ? (
        <pre className="mono text-xs bg-ink-950 border border-ink-800 rounded-md p-3 overflow-auto max-h-96 whitespace-pre-wrap break-all">
          {result.sample}
        </pre>
      ) : candidates.length === 0 ? (
        <Empty>
          Nothing to pick from automatically — use the Raw tab and write an expression by hand.
        </Empty>
      ) : (
        <>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`Filter ${candidates.length} values…`}
            className="w-full bg-ink-950 border border-ink-800 rounded-md px-2.5 py-1.5 text-sm placeholder:text-ink-700 focus:outline-none focus:border-accent/70"
          />
          <div className="border border-ink-800 rounded-md divide-y divide-ink-800 max-h-96 overflow-auto">
            {visible.map((c) => (
              <button
                key={c.expr}
                onClick={() => onPick({ extractor: c.extractor, expr: c.expr, sample: c.sample })}
                className="w-full text-left px-3 py-1.5 hover:bg-ink-800 flex items-baseline gap-3 group"
              >
                <span className="mono text-xs text-accent truncate flex-1">{c.expr}</span>
                <span className="mono text-xs text-ink-300 truncate max-w-[45%]">{c.sample}</span>
                <span className="text-xs text-ink-700 group-hover:text-ink-300 shrink-0">use</span>
              </button>
            ))}
            {visible.length === 0 && <Empty>No match.</Empty>}
          </div>
        </>
      )}

      {tab === 'raw' && result.sample && (
        <div className="flex justify-end">
          <Copy text={result.sample} label="Copy payload" />
        </div>
      )}
    </div>
  );
}
